import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, AlertOctagon, Info, RefreshCcw } from "lucide-react";
import { useAlertsCenter, ALERT_CATEGORY_LABEL, ALERT_CATEGORY_ROUTE, AlertCategory, AlertSeverity } from "@/api/alerts";

const SEVERITY_STYLE: Record<AlertSeverity, { badge: string; row: string; icon: typeof AlertTriangle }> = {
  CRITICAL: { badge: "bg-red-100 text-red-700", row: "border-l-4 border-red-500", icon: AlertOctagon },
  WARNING: { badge: "bg-amber-100 text-amber-700", row: "border-l-4 border-amber-400", icon: AlertTriangle },
  INFO: { badge: "bg-gray-100 text-gray-600", row: "border-l-4 border-gray-300", icon: Info },
};

const SEVERITY_LABEL: Record<AlertSeverity, string> = { CRITICAL: "Crítica", WARNING: "Atención", INFO: "Info" };

const CATEGORY_ORDER = Object.keys(ALERT_CATEGORY_LABEL) as AlertCategory[];

/**
 * Centro de Alertas: junta en una sola vista lo que antes había que revisar
 * pantalla por pantalla (stock, lotes, cuentas por pagar, márgenes, rotación).
 * Todo se calcula en vivo en el backend (alerts.service.ts) — no hay
 * alertas "guardadas" que puedan quedar desactualizadas. Se refresca sola
 * cada 5 minutos (ver useAlertsCenter) además del botón manual.
 */
export default function AlertsPage() {
  const [activeCategory, setActiveCategory] = useState<AlertCategory | null>(null);
  const { data, isLoading, isFetching, refetch } = useAlertsCenter();

  const items = data?.items ?? [];
  const filtered = activeCategory ? items.filter((i) => i.category === activeCategory) : items;

  const criticalCount = items.filter((i) => i.severity === "CRITICAL").length;
  const warningCount = items.filter((i) => i.severity === "WARNING").length;
  const infoCount = items.filter((i) => i.severity === "INFO").length;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Alertas</h1>
          <p className="text-sm text-gray-500">Todo lo que necesita atención hoy: stock, vencimientos, cuentas por pagar, márgenes y rotación — calculado en vivo.</p>
        </div>
        <button type="button" className="btn-secondary flex items-center gap-1.5" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCcw size={14} className={isFetching ? "animate-spin" : ""} /> Actualizar
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="card p-4">
          <p className="text-xs font-medium uppercase text-red-600">Críticas</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{criticalCount}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs font-medium uppercase text-amber-600">Atención</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{warningCount}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs font-medium uppercase text-gray-500">Informativas</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{infoCount}</p>
        </div>
      </div>

      <div className="card flex flex-wrap items-center gap-2 p-3">
        <button
          type="button"
          className={`rounded-full px-3 py-1 text-xs font-medium ${activeCategory === null ? "bg-brand-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
          onClick={() => setActiveCategory(null)}
        >
          Todas ({items.length})
        </button>
        {CATEGORY_ORDER.map((cat) => {
          const count = data?.counts[cat] ?? 0;
          if (count === 0) return null;
          return (
            <button
              key={cat}
              type="button"
              className={`rounded-full px-3 py-1 text-xs font-medium ${activeCategory === cat ? "bg-brand-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
              onClick={() => setActiveCategory(cat)}
            >
              {ALERT_CATEGORY_LABEL[cat]} ({count})
            </button>
          );
        })}
      </div>

      <div className="card overflow-hidden">
        {isLoading && <p className="p-6 text-center text-sm text-gray-400">Cargando...</p>}
        {!isLoading && filtered.length === 0 && (
          <div className="flex flex-col items-center gap-2 p-10 text-center text-gray-400">
            <AlertTriangle size={28} className="opacity-30" />
            <p className="text-sm">{items.length === 0 ? "Sin alertas activas — todo en orden." : "Sin alertas en esta categoría."}</p>
          </div>
        )}
        <ul className="divide-y divide-gray-100">
          {filtered.map((item, idx) => {
            const style = SEVERITY_STYLE[item.severity];
            const Icon = style.icon;
            return (
              <li key={`${item.category}-${item.entityId}-${idx}`} className={`flex items-start gap-3 px-4 py-3 ${style.row}`}>
                <Icon size={18} className="mt-0.5 shrink-0 text-gray-400" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${style.badge}`}>{SEVERITY_LABEL[item.severity]}</span>
                    <span className="text-xs font-medium uppercase tracking-wide text-gray-400">{ALERT_CATEGORY_LABEL[item.category]}</span>
                  </div>
                  <p className="mt-1 text-sm font-medium text-gray-900">{item.title}</p>
                  <p className="text-sm text-gray-600">{item.message}</p>
                </div>
                <Link to={ALERT_CATEGORY_ROUTE[item.category]} className="shrink-0 text-xs font-medium text-brand-600 hover:text-brand-800">
                  Ver
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
